import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import './BookAppointment.css';

const BookAppointment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    serviceType: '',
    description: ''
  });

  // Get service type from URL parameters and scroll to top
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const service = urlParams.get('service');
    if (service) {
      setFormData(prev => ({ ...prev, serviceType: service }));
    }
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [location.search]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.custom-date-picker')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);


  // Get tomorrow's date for minimum date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Function to disable Sundays (day 0)
  const isSunday = (dateString) => {
    const date = new Date(dateString);
    return date.getDay() === 0; // Sunday is day 0
  };

  // Handle custom date picker
  const handleDateSelect = (day) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Create date string manually to avoid timezone issues
    const dateString = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    
    if (isSunday(dateString)) {
      // Automatically advance to Monday
      const nextDay = day + 1;
      const nextDateString = `${year}-${(month + 1).toString().padStart(2, '0')}-${nextDay.toString().padStart(2, '0')}`;
      setSelectedDate(nextDateString);
    } else {
      setSelectedDate(dateString);
    }
    setShowDatePicker(false);
  };

  // Format date for display in American format
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    // Parse the date string directly to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  };

  // Format date in American style (mm/dd/yyyy)
  const formatAmericanDate = (dateString) => {
    // Parse the date string directly to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  };

  // Generate calendar links for the appointment
  const generateCalendarLinks = (appointmentDate, serviceType, clientName) => {
    const date = new Date(appointmentDate + 'T09:00:00'); // 9 AM appointment
    const endDate = new Date(appointmentDate + 'T10:00:00'); // 1 hour duration
    
    // Format dates for calendar links (YYYYMMDDTHHMMSSZ)
    const formatCalendarDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const startTime = formatCalendarDate(date);
    const endTime = formatCalendarDate(endDate);
    
    // Create event details
    const title = `NJ-MCS Appointment - ${serviceType}`;
    const description = `Construction service appointment with ${clientName} for ${serviceType}`;
    const location = 'Pennsylvania, New York, New Jersey';
    
    // Google Calendar link
    const googleCalendarLink = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startTime}/${endTime}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;
    
    // Outlook Calendar link
    const outlookCalendarLink = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(title)}&startdt=${startTime}&enddt=${endTime}&body=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;
    
    // Apple Calendar link (works on iOS) - using Calndr.link service
    const appleCalendarLink = `https://calndr.link/e?title=${encodeURIComponent(title)}&start=${startTime}&end=${endTime}&description=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;
    
    return {
      google: googleCalendarLink,
      outlook: outlookCalendarLink,
      apple: appleCalendarLink
    };
  };

  // Get next available non-Sunday date
  const getNextAvailableDate = () => {
    let date = new Date();
    date.setDate(date.getDate() + 1); // Start from tomorrow
    
    // Find the next non-Sunday date
    while (date.getDay() === 0) { // While it's Sunday
      date.setDate(date.getDate() + 1);
    }
    
    return date.toISOString().split('T')[0];
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Convert Sunday (0) to 7, then adjust for Monday start
    let startingDayOfWeek = firstDay.getDay();
    if (startingDayOfWeek === 0) startingDayOfWeek = 7; // Sunday becomes 7
    startingDayOfWeek = startingDayOfWeek - 1; // Adjust for Monday start (0-6)
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isPast = date < new Date();
      const isSunday = date.getDay() === 0;
      const isToday = date.toDateString() === new Date().toDateString();
      
      // Create date string manually to avoid timezone issues
      const dateString = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      
      days.push({
        day,
        date: dateString,
        isPast,
        isSunday,
        isToday
      });
    }
    
    return days;
  };

  // Navigate months
  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      alert('Please select a date for your appointment.');
      return;
    }

    if (isSunday(selectedDate)) {
      alert('Sorry, we are closed on Sundays. Please select a different date.');
      return;
    }

    // EmailJS configuration
    const serviceId = 'service_3f8b781';
    const templateId = 'template_lcpkfgf';
    const publicKey = 'wtbiVbDXZvVWC9o4s';


    // Format the date
    const formattedDate = formatAmericanDate(selectedDate);
    console.log('Original date:', selectedDate);
    console.log('Formatted date:', formattedDate);

    // Generate calendar links
    const calendarLinks = generateCalendarLinks(selectedDate, formData.serviceType, formData.fullName);
    console.log('Generated calendar links:', calendarLinks);
    console.log('Apple Calendar link type:', typeof calendarLinks.apple);
    console.log('Apple Calendar link preview:', calendarLinks.apple.substring(0, 100) + '...');

    // Prepare appointment data
    const appointmentData = {
      from_name: formData.fullName,
      from_email: formData.email,
      phone: formData.phone,
      service_type: formData.serviceType,
      appointment_date: formattedDate,
      description: formData.description,
      to_email: 'info.njmcs@gmail.com',
      // Calendar links
      google_calendar_link: calendarLinks.google,
      outlook_calendar_link: calendarLinks.outlook,
      apple_calendar_link: calendarLinks.apple
    };

                // Send email with appointment details
                emailjs.send(serviceId, templateId, appointmentData, publicKey)
                  .then((response) => {
                    console.log('Appointment booked successfully!', response.status, response.text);
                    setShowSuccessModal(true);
                  })
                  .catch((error) => {
                    console.error('Error booking appointment:', error);
                    alert('Sorry, there was an error booking your appointment. Please try again or call us directly.');
                  });
  };

  return (
    <div className="book-appointment">
      <div className="appointment-hero">
        <div className="container">
          <h1>Book Your Appointment</h1>
          <p>Schedule your consultation today</p>
        </div>
      </div>

      <div className="appointment-content">
        <div className="container">
          <div className="appointment-form-container">
            <form onSubmit={handleSubmit} className="appointment-form">
              <div className="form-section">
                <h3>Personal Information</h3>
                
                <div className="form-group">
                  <label htmlFor="fullName">Full Name *</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Service Details</h3>
                
                <div className="form-group">
                  <label htmlFor="serviceType">Service Type</label>
                  <input
                    type="text"
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    readOnly
                    className="readonly-field"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Project Description *</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Please describe your project requirements..."
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Schedule Appointment</h3>
                
                <div className="form-group">
                  <label htmlFor="appointmentDate">Preferred Date * (MM/DD/YYYY)</label>
                  <div className="custom-date-picker">
                    <input
                      type="text"
                      id="appointmentDate"
                      value={formatDateForDisplay(selectedDate)}
                      placeholder="MM/DD/YYYY"
                      readOnly
                      onClick={() => setShowDatePicker(!showDatePicker)}
                      required
                    />
                    <button 
                      type="button" 
                      className="calendar-icon"
                      onClick={() => setShowDatePicker(!showDatePicker)}
                    >
                      📅
                    </button>
                    
                    {showDatePicker && (
                      <div className="date-picker-dropdown">
                        <div className="date-picker-header">
                          <button 
                            type="button" 
                            onClick={() => navigateMonth(-1)}
                            className="nav-button"
                          >
                            ‹
                          </button>
                          <h3>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
                          <button 
                            type="button" 
                            onClick={() => navigateMonth(1)}
                            className="nav-button"
                          >
                            ›
                          </button>
                        </div>
                        
                        <div className="date-picker-weekdays">
                          <div className="weekday">Mon</div>
                          <div className="weekday">Tue</div>
                          <div className="weekday">Wed</div>
                          <div className="weekday">Thu</div>
                          <div className="weekday">Fri</div>
                          <div className="weekday">Sat</div>
                          <div className="weekday sunday-disabled">Sun</div>
                        </div>
                        
                        <div className="date-picker-days">
                          {generateCalendarDays().map((dayData, index) => {
                            if (!dayData) {
                              return <div key={index} className="empty-day"></div>;
                            }
                            
                            const isSelected = dayData.date === selectedDate;
                            const isDisabled = dayData.isPast || dayData.isSunday;
                            
                            return (
                              <button
                                key={index}
                                type="button"
                                className={`calendar-day ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${dayData.isToday ? 'today' : ''}`}
                                onClick={() => !isDisabled && handleDateSelect(dayData.day)}
                                disabled={isDisabled}
                              >
                                {dayData.day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>


              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  Book Appointment
                </button>
                <button type="button" onClick={() => navigate('/')} className="cancel-btn">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#4CAF50"/>
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Appointment Booked Successfully!</h2>
            <p>Thank you, <strong>{formData.fullName}</strong>!</p>
            <p>Your appointment for <strong>{formData.serviceType}</strong> on <strong>{formatDateForDisplay(selectedDate)}</strong> has been confirmed.</p>
            <p>We will contact you soon to finalize the details.</p>
            <button 
              className="continue-btn" 
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/');
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookAppointment;
